<script type="text/jsx" lang="jsx">
import { mapGetters } from 'vuex';
import { getSoftBundlePromotionInfo } from '@/views/config/coupon/helper/promotion';
import { couponUseCaseSignMap } from '@/views/config/coupon/helper/constant';
import { getMessageTemplateByRules } from '@/views/config/partner-refer/helper/validator';
import { dateTimeFormatWithClientZone } from '@/utils/dateformat';
import { isEmptyVal } from '@/utils';
import { PROMOTION_TYPE } from '@/views/config/coupon/helper/constant';
import AltProratedPlanDiscount from '@/views/config/shopping-cart/prorated/plan-discount.vue';
import VAmountLoading from '@/components/v-amount-loading.vue';
import { couponSourceTypeMap } from '@/views/config/coupon/helper/constant';

export default {
  name: 'PlanDiscountList',
  components: {
    AltProratedPlanDiscount,
    VAmountLoading,
  },
  props: {
    planCode: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['discountWithAmountMap', 'couponConfiguration', 'partnerRefer', 'eligiblePartnerReferOffer', 'computedAmountLoading', 'couponState']),
    // This logic can be decoupled by extracting the discount item generation into separate pure functions.
    // This will make the code more maintainable and testable.
    discountItems() {
      // Get soft bundle discount item
      const softBundleItem = this._getSoftBundleDiscountItem();
      // Get coupon discount item
      const couponItem = this._getCouponDiscountItem();

      // Filter out null items and return as array
      return [softBundleItem, couponItem].filter(Boolean);
    },
  },
  methods: {
    _get_discount_expire_date(expireDate, couponUseCaseSign) {
      const hiddenExpireDate = couponUseCaseSign === couponUseCaseSignMap.softBundleNoExpire
        || this.couponConfiguration?.cartView?.hiddenExpireDate;
      if (hiddenExpireDate) return this.$t('billing.coupon.discount');

      // partner with exclusive cart discount label
      if (!isEmptyVal(this.partnerRefer) && this.eligiblePartnerReferOffer) {
        const messageBody = getMessageTemplateByRules(this.partnerRefer?.cartDiscountLabel)?.messageBody;
        if (messageBody) return messageBody;
      }

      const expiresDesc = expireDate ?
        ` (${this.$t('billing.coupon.expiresDesc', [dateTimeFormatWithClientZone(expireDate)])})` : '';

      return `${this.$t('billing.coupon.discount')} ${expiresDesc}`;
    },
    // Get promotion discount item for current planCode
    _getSoftBundleDiscountItem() {
      const { discountPlans } = getSoftBundlePromotionInfo()?.discountInfo ?? {};
      const softBundleDiscount = discountPlans?.[this.planCode]?.discountAmount ?? 0;

      if (softBundleDiscount) {
        return {
          key: PROMOTION_TYPE.softBundle,
          label: this.$t('billing.coupon.discount'),
          discount: softBundleDiscount,
          planCode: this.planCode
        };
      }
      return null;
    },

    // Get coupon discount item for current planCode
    _getCouponDiscountItem() {
      // if is threshold coupon, return null
      if (this.couponState?.couponSourceType === couponSourceTypeMap.threshold) return null;

      const { discountPlans: couponDiscountPlans } = this.discountWithAmountMap ?? {};
      const couponDiscount = couponDiscountPlans?.[this.planCode]?.discountAmount ?? 0;

      if (couponDiscount) {
        const expireDate = couponDiscountPlans?.[this.planCode]?.expireDate ?? '';
        const couponUseCaseSign = couponDiscountPlans?.[this.planCode]?.couponUseCaseSign;
        const label = this._get_discount_expire_date(expireDate, couponUseCaseSign);

        return {
          key: PROMOTION_TYPE.coupon,
          label,
          discount: couponDiscount,
          planCode: this.planCode
        };
      }
      return null;
    },
    renderDiscountItem(item) {
      return <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__" key={item.key}>
        <VAmountLoading><span>{item.label}</span></VAmountLoading>
        <AltProratedPlanDiscount code={item.planCode}>
          <VAmountLoading>
            <span class="opc-cart-item__value text-no-capitalize">{this.$money(item.discount)}</span>
          </VAmountLoading>
        </AltProratedPlanDiscount>
      </div>
    },
  },
  render() {
    if (!this.discountItems?.length) return null;

    // todo [soft bundle] only show one item when loading
    // multiple discount
    const discountItems = this.computedAmountLoading ? [this.discountItems[0]] : this.discountItems;

    const list = discountItems.map((item) => {
      return this.renderDiscountItem(item);
    });
    return <div class="opc-cart-item__plan-discount-wrapper">{list}</div>;
  },
};
</script>

<style lang="scss">
.opc-cart-item {
  &__plan-discount-wrapper {

  }
}
</style>
